import { Stack } from "@mui/material"
import { useReferenceState } from "lib/@hooks/use-reference"
import { ResolutionFilterDropdown } from "./resolution-list-filter-dropdown"

const STATUS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "complete", label: "Ready" },
    { value: "error", label: "Error" },
    { value: "applied", label: "Applied" },
]

const CHANGES_OPTIONS = [
    { value: "all", label: "Any" },
    { value: "has-changes", label: "Has changes" },
    { value: "no-changes", label: "No changes" },
]

const DECISIONS_OPTIONS = [
    { value: "all", label: "Any" },
    { value: "all-decided", label: "All decided" },
    { value: "undecided", label: "Has undecided" },
]

const FLAG_OPTIONS = [
    { value: "all", label: "All" },
    { value: "flagged", label: "Flagged for review" },
    { value: "unflagged", label: "Not flagged" },
]

const TYPE_OPTIONS = [
    { value: "all", label: "Any" },
    { value: "add_task", label: "Added tasks" },
    { value: "remove_task", label: "Deleted tasks" },
    { value: "edit_content", label: "Edited content" },
    { value: "edit_notes", label: "Edited notes" },
]

const CRITICALITY_OPTIONS = [
    { value: "all", label: "All" },
    { value: "not-set", label: "Not set" },
    { value: "red", label: "Red" },
    { value: "amber", label: "Amber" },
    { value: "green", label: "Green" },
]

const EDIT_TYPES = new Set(["intro_content", "task_content", "task_title"])
const NOTE_TYPES = new Set(["intro_note", "task_note"])

export function useResolutionFilters() {
    const [status, setStatus] = useReferenceState("resolution-filter-status", "all")
    const [changes, setChanges] = useReferenceState("resolution-filter-changes", "all")
    const [decisions, setDecisions] = useReferenceState("resolution-filter-decisions", "all")
    const [flag, setFlag] = useReferenceState("resolution-filter-flag", "all")
    const [type, setType] = useReferenceState("resolution-filter-type", "all")
    const [criticality, setCriticality] = useReferenceState("resolution-filter-criticality", "all")
    return {
        status,
        setStatus,
        changes,
        setChanges,
        decisions,
        setDecisions,
        flag,
        setFlag,
        type,
        setType,
        criticality,
        setCriticality,
    }
}

export function applyResolutionFilters(items, { status, changes, decisions, flag, type, criticality }) {
    return items.filter((item) => {
        if (status !== "all" && item.status !== status) return false

        const changeList = item.proposedChanges ?? []
        const changeCount = changeList.length
        if (changes === "has-changes" && changeCount === 0) return false
        if (changes === "no-changes" && changeCount > 0) return false

        if (decisions !== "all") {
            const undecided = changeList.filter((c) => c.decision === null || c.decision === undefined).length
            if (decisions === "all-decided" && undecided > 0) return false
            if (decisions === "undecided" && undecided === 0) return false
        }

        if (flag === "flagged" && !item.flaggedForReview) return false
        if (flag === "unflagged" && item.flaggedForReview) return false

        if (type !== "all") {
            const has =
                type === "add_task"
                    ? changeList.some((c) => c.changeType === "add_task")
                    : type === "remove_task"
                      ? changeList.some((c) => c.changeType === "remove_task")
                      : type === "edit_content"
                        ? changeList.some((c) => EDIT_TYPES.has(c.changeType))
                        : type === "edit_notes"
                          ? changeList.some((c) => NOTE_TYPES.has(c.changeType))
                          : true
            if (!has) return false
        }

        if (criticality !== "all") {
            if (criticality === "not-set" && item.criticality) return false
            if (criticality !== "not-set" && item.criticality !== criticality) return false
        }

        return true
    })
}

export function ResolutionListFilterDropdowns() {
    const {
        status,
        setStatus,
        changes,
        setChanges,
        decisions,
        setDecisions,
        flag,
        setFlag,
        type,
        setType,
        criticality,
        setCriticality,
    } = useResolutionFilters()
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <ResolutionFilterDropdown label="Status" options={STATUS_OPTIONS} value={status} onChange={setStatus} />
            <ResolutionFilterDropdown label="Changes" options={CHANGES_OPTIONS} value={changes} onChange={setChanges} />
            <ResolutionFilterDropdown
                label="Decisions"
                options={DECISIONS_OPTIONS}
                value={decisions}
                onChange={setDecisions}
            />
            <ResolutionFilterDropdown label="Flag" options={FLAG_OPTIONS} value={flag} onChange={setFlag} />
            <ResolutionFilterDropdown label="Type" options={TYPE_OPTIONS} value={type} onChange={setType} />
            <ResolutionFilterDropdown
                label="Criticality"
                options={CRITICALITY_OPTIONS}
                value={criticality}
                onChange={setCriticality}
            />
        </Stack>
    )
}
